import { events } from "@constants/tracking";
import { useSessionConfigDialog } from "@contexts/DialogContext/dialogs/SessionConfigDialog";
import { useRecommendedSessionDialog } from "@features/dashboard/hooks/useRecommendedSessionDialog";
import { buildFrontendRecommendationOptions } from "@features/dashboard/recommendations";
import { buildRecommendedSessionChips } from "@features/dashboard/recommendations/adapters/recommended-session-dialog";
import { useHydratedStudyRecommendations } from "@features/dashboard/recommendations/hooks/useHydratedStudyRecommendations";
import { RecommendedCard } from "@memorang/ui/components/HomeApplet/RecommendedCard";
import { RecommendedCardSkeleton } from "@memorang/ui/components/HomeApplet/RecommendedCardSkeleton";
import {
	getSkillIcon,
	questionTypeIconMap,
	skillIconMap,
} from "@memorang/ui/constants";
import { useProficiencyIconMap } from "@memorang/ui/hooks/useProficiencyIconMap";
import { trackCustomEvent } from "analytics";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	buildNextActionFallbackOptions,
	resolveNextActionPresentation,
} from "../helpers/next-action";

const RECOMMENDED_SESSION_MINUTES = 10;

type Options = {
	sessionId: string;
	skillTagIds: number[];
	weakestQuestionTypeId?: number;
	sessionTagIds: number[];
	isSummativeTest: boolean;
};

export const useSessionReportNextAction = ({
	sessionId,
	skillTagIds,
	weakestQuestionTypeId,
	sessionTagIds,
	isSummativeTest,
}: Options) => {
	const { t } = useTranslation();
	const { showSessionConfig } = useSessionConfigDialog();
	const proficiencyIconMap = useProficiencyIconMap();

	const hydrated = useHydratedStudyRecommendations({
		sessionCount: 1,
		durationMinutes: RECOMMENDED_SESSION_MINUTES,
		filterValueIds: skillTagIds,
	});

	const { openRecommendedSession, recommendedSessionDialog } =
		useRecommendedSessionDialog("session_report");

	const option = useMemo(() => {
		const presentation = resolveNextActionPresentation({
			state: hydrated,
			skillTagIds,
			weakestQuestionTypeId,
		});
		return hydrated.status === "READY" && presentation
			? buildFrontendRecommendationOptions({
					result: presentation,
					skills: hydrated.frontend.skills,
					proficiencies: hydrated.frontend.proficiencies,
					questionTypes: hydrated.frontend.questionTypes,
					translate: t,
					proficiencyIconMap,
					skillIconMap,
					questionTypeIconMap,
				})[0]
			: undefined;
	}, [hydrated, skillTagIds, weakestQuestionTypeId, t, proficiencyIconMap]);

	const isLoading = hydrated.status === "LOADING";
	const cellId = option?.id;
	const viewedRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		const viewKey = `${sessionId}:${cellId ?? "fallback"}`;
		if (isLoading || viewedRef.current === viewKey) {
			return;
		}
		viewedRef.current = viewKey;
		trackCustomEvent({
			eventName: events.sessionReportNextActionViewed,
			view: "session_report",
			session_id: sessionId,
			recommended: cellId ? 1 : 0,
			...(cellId ? { cell_id: cellId } : {}),
		});
	}, [cellId, isLoading, sessionId]);

	const trackStart = () => {
		trackCustomEvent({
			eventName: events.sessionReportNextActionStartClicked,
			view: "session_report",
			session_id: sessionId,
			recommended: cellId ? 1 : 0,
			...(cellId ? { cell_id: cellId } : {}),
		});
	};

	const renderSection = () => {
		if (isLoading) {
			return <RecommendedCardSkeleton />;
		}

		if (!option) {
			return (
				<RecommendedCard
					status="NOT_STARTED"
					kind="session"
					ctaMode="contained"
					title={t("report.nextAction.fallback.title")}
					body={t("report.nextAction.fallback.description")}
					onPress={() => {
						trackStart();
						showSessionConfig(
							buildNextActionFallbackOptions({
								isSummativeTest,
								sessionTagIds,
							}),
						);
					}}
				/>
			);
		}

		const { skill, questionTypes } = option.task.recommendation;

		return (
			<RecommendedCard
				status="NOT_STARTED"
				kind="session"
				ctaMode="contained"
				icon={getSkillIcon(skill.title)}
				title={t("report.nextAction.title", {
					focus: questionTypes?.[0]?.title ?? skill.title,
				})}
				body={option.insight}
				chips={buildRecommendedSessionChips(option.task.quickStudyMeta, t)}
				onPress={() => {
					trackStart();
					openRecommendedSession(option.task);
				}}
			/>
		);
	};

	return {
		nextActionSlot: renderSection(),
		nextActionDialog: recommendedSessionDialog,
	};
};
